import React from 'react';
import { alpha, Box, Button, Typography } from '@mui/material';
import { InformationChip, useL2DTheme } from '@europrocurement/flexy-components';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { TiersContact } from '@europrocurement/l2d-modules/providers/TiersProvider';
import { PeoplePreview } from '@europrocurement/l2d-modules';

export type PrescriberMainContactProps = {
    contact: TiersContact;
};

const PrescriberMainContact: React.FunctionComponent<PrescriberMainContactProps> = function ({
    contact,
}) {
    const { palette } = useL2DTheme();
    const fullName = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
    const phone = contact.phone || contact.mobile;

    return (
        <Box sx={{ p: '0 24px 24px' }}>
            <Box
                sx={{
                    border: `1px solid ${palette.divider}`,
                    borderRadius: 1,
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: alpha(palette.primary.light, 0.1),
                        borderColor: alpha(palette.primary.light, 0.2),
                    },
                }}
            >
                <PeoplePreview
                    firstName={contact.firstName}
                    lastName={contact.lastName}
                    sxAvatar={{
                        width: 40,
                        height: 40,
                    }}
                    auth={false}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                            mb: 0.5,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 600 }}
                        >
                            {fullName}
                        </Typography>
                        <InformationChip
                            label="Principal"
                            color="primary"
                            sx={{ fontSize: '10px', padding: '2px 6px', height: '20px' }}
                        />
                    </Box>
                    {contact.userIdentifier && (
                        <Box sx={{ mb: 0.5 }}>
                            <InformationChip
                                icon={['fasl', 'check']}
                                label="Accès plateforme"
                                color="success"
                                sx={{ fontSize: '10px', padding: '2px 6px', height: '20px' }}
                            />
                        </Box>
                    )}
                    {contact.fonction && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mb: contact.accounting ? 0.5 : 0 }}
                        >
                            {contact.fonction}
                        </Typography>
                    )}
                    {contact.accounting && (
                        <InformationChip
                            label="Service : Comptabilité"
                            color="warning"
                            sx={{ fontSize: '10px', padding: '2px 6px', height: '20px' }}
                        />
                    )}
                </Box>
                <FaOptionIcon
                    icon={['fasl', 'chevron-right']}
                    sx={{ fontSize: '12px', color: palette.text.secondary, flexShrink: 0, mt: 0.5 }}
                />
            </Box>

            {(contact.email || contact.phone || contact.mobile) && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {contact.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <FaOptionIcon
                                icon={['fasl', 'at']}
                                sx={{ fontSize: '14px', color: palette.text.secondary, width: 16 }}
                            />
                            <Typography variant="body2">{contact.email}</Typography>
                        </Box>
                    )}
                    {contact.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <FaOptionIcon
                                icon={['fasl', 'phone']}
                                sx={{ fontSize: '14px', color: palette.text.secondary, width: 16 }}
                            />
                            <Typography variant="body2">{contact.phone}</Typography>
                        </Box>
                    )}
                    {contact.mobile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <FaOptionIcon
                                icon={['fasl', 'mobile']}
                                sx={{ fontSize: '14px', color: palette.text.secondary, width: 16 }}
                            />
                            <Typography variant="body2">
                                {contact.mobile}{' '}
                                <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    (mobile)
                                </Typography>
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {(phone || contact.email) && (
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    {phone && (
                        <Button
                            variant="contained"
                            size="small"
                            component="a"
                            href={`tel:${phone}`}
                            startIcon={
                                <FaOptionIcon
                                    icon={['fasl', 'phone']}
                                    sx={{ fontSize: '12px !important' }}
                                />
                            }
                            sx={{ borderRadius: 6, px: 2, textTransform: 'none' }}
                        >
                            Appeler
                        </Button>
                    )}
                    {contact.email && (
                        <Button
                            variant="outlined"
                            size="small"
                            component="a"
                            href={`mailto:${contact.email}`}
                            sx={{ borderRadius: 6, px: 2, textTransform: 'none' }}
                        >
                            @ E-mail
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default PrescriberMainContact;
